import { useState } from 'react';
import { api } from '../api.js';
import { t, getal, wanneer } from '../i18n.js';

/**
 * Beheer › Beheerders, als lijst met detail (zoals Beheer › Klanten): links de medewerkers met hun rollen in het kort,
 * rechts de gekozen beheerder met e-mailadres, actief, rollen, klanten en de rechten die daaruit volgen.
 */
export default function BeheerBeheerders({ employees, rollen, tenants, permissions, mij, doe }) {
  const [gekozen, zetGekozen] = useState(employees.find((m) => m.number !== mij.number)?.number ?? employees[0]?.number);
  const [zoek, zetZoek] = useState('');
  const m = employees.find((x) => x.number === gekozen);
  const rolNaam = (code) => t(rollen.find((r) => r.code === code)?.label ?? code);
  const lijst = employees.filter((x) => !zoek.trim() || `${x.name} ${x.number} ${x.email ?? ''}`.toLowerCase().includes(zoek.trim().toLowerCase()))
    .sort((a, b) => Number(b.active) - Number(a.active) || String(a.name || a.number).localeCompare(String(b.name || b.number)));

  return (
    <div className="klantbeheer">
      <section className="card klantlijst">
        <div className="klantlijst-kop">
          <input className="input" value={zoek} onChange={(e) => zetZoek(e.target.value)} placeholder={t('Zoek een beheerder')} aria-label={t('Zoek een beheerder')} />
        </div>
        <div className="klantlijst-rijen">
          {lijst.map((x) => (
            <button key={x.number} type="button" className={`klantrij ${x.number === gekozen ? 'on' : ''}`} onClick={() => zetGekozen(x.number)}>
              <span className="klantrij-naam"><b>{x.name || x.number}</b>
                {x.number === mij.number && <span className="pill">{t('jij')}</span>}
                {x.emergency && <span className="pill wacht">{t('noodingang')}</span>}
                {!x.active && <span className="pill wacht">{t('uit')}</span>}</span>
              <span className="klantrij-cijfers">
                <span>{x.roles.length ? x.roles.map(rolNaam).join(', ') : t('nog geen rol')}</span>
                <span>{t('{n} klanten', { n: getal(x.tenants.length) })}</span>
              </span>
            </button>
          ))}
          {!lijst.length && <p className="empty">{t('Niemand gevonden.')}</p>}
        </div>
        <p className="noot" style={{ margin: '10px 4px 0' }}>{t('Een nieuw ADTS-account verschijnt hier na de eerste aanmelding, zonder rol.')}</p>
      </section>
      <section className="card klantdetail">
        {m ? <Detail key={m.number} m={m} rollen={rollen} tenants={tenants} permissions={permissions} mij={mij} doe={doe} /> : <p className="empty">{t('Kies links een beheerder.')}</p>}
      </section>
    </div>
  );
}

function Detail({ m, rollen, tenants, permissions, mij, doe }) {
  const [mail, zetMail] = useState(null);
  const wissel = (lijst, waarde, aan) => (aan ? [...lijst, waarde] : lijst.filter((x) => x !== waarde));
  const zet = (invoer) => doe(() => api.wijzigMedewerker(m.number, invoer));
  const rechten = [...new Set(m.roles.flatMap((r) => rollen.find((x) => x.code === r)?.permissions ?? []))];
  const rechtLabel = (id) => t(permissions.find((p) => p.id === id)?.label ?? id);
  const vink = (aan, label, onChange) => <label className="vink"><input type="checkbox" checked={aan} onChange={(e) => onChange(e.target.checked)} />{label}</label>;

  return (
    <>
      <header className="klantdetail-kop">
        <div><h2 style={{ margin: 0 }}>{m.name || m.number}</h2>
          <span className="muted mono" style={{ fontSize: 12.5 }}>{m.number}</span>
          <span className="muted" style={{ fontSize: 12.5 }}> · {m.lastSignInAt ? t('laatst aangemeld {wanneer}', { wanneer: wanneer(m.lastSignInAt) }) : t('nog niet aangemeld')}</span></div>
        <label className="vink schakel"><input type="checkbox" checked={m.active} disabled={m.number === mij.number && m.active}
          onChange={(e) => zet({ active: e.target.checked })} />{m.active ? t('Actief') : t('Uit')}</label>
      </header>

      <div className="detailblok">
        <h3>{t('E-mailadres')}</h3>
        {mail !== null ? (
          <form className="rij" style={{ gap: 6 }} onSubmit={(e) => { e.preventDefault(); doe(async () => { await api.wijzigMedewerker(m.number, { email: mail.trim() }); zetMail(null); }); }}>
            <input className="input" type="email" value={mail} autoFocus aria-label={t('E-mailadres')} onChange={(e) => zetMail(e.target.value)} placeholder="naam@adts.nl" />
            <button className="btn small primary">{t('Opslaan')}</button>
            <button type="button" className="btn small" onClick={() => zetMail(null)}>{t('Annuleren')}</button>
          </form>
        ) : (
          <p style={{ margin: 0 }}>{m.email || <span className="muted">{t('geen e-mailadres')}</span>}{' '}
            <button type="button" className="namelink" onClick={() => zetMail(m.email ?? '')}>{t('Wijzigen')}</button></p>
        )}
        <p className="noot" style={{ margin: '4px 0 0' }}>{t('Het e-mailadres koppelt een beheerder aan zijn ADTS-account bij de eerste aanmelding; wie al gekoppeld is, blijft gekoppeld.')}</p>
      </div>

      <div className="detailblok">
        <h3>{t('Rollen')}</h3>
        <div className="vinkraster">{rollen.map((r) => <span key={r.code}>{vink(m.roles.includes(r.code), t(r.label), (aan) => zet({ roles: wissel(m.roles, r.code, aan) }))}</span>)}</div>
        <p className="noot" style={{ margin: '6px 0 0' }}>{t('Rollen tellen op.')}</p>
      </div>

      <div className="detailblok">
        <h3>{t('Klanten')} <span className="muted" style={{ fontWeight: 'normal', fontSize: 12.5 }}>{t('{n} van {m}', { n: m.tenants.length, m: tenants.length })}</span>
          <span style={{ float: 'right', fontWeight: 'normal', fontSize: 12.5 }}>
            <button type="button" className="namelink" onClick={() => zet({ tenants: tenants.map((k) => k.code) })}>{t('alle')}</button>{' · '}
            <button type="button" className="namelink" onClick={() => zet({ tenants: [] })}>{t('geen')}</button></span></h3>
        <div className="vinkraster">{tenants.map((k) => <span key={k.code}>{vink(m.tenants.includes(k.code), k.name, (aan) => zet({ tenants: wissel(m.tenants, k.code, aan) }))}</span>)}</div>
        <p className="noot" style={{ margin: '6px 0 0' }}>{t('Klanten bepalen waar iemand zijn rechten mag gebruiken; ook "Alle klanten" betekent alleen de klanten die hier zijn aangevinkt.')}</p>
      </div>

      <div className="detailblok">
        <h3>{t('Rechten')} <span className="muted" style={{ fontWeight: 'normal', fontSize: 12.5 }}>{t('uit de rollen, opgeteld')}</span></h3>
        {rechten.length ? <div className="rechtenlijst">{rechten.map((r) => <span key={r} className="ttag">{rechtLabel(r)}</span>)}</div> : <p className="muted" style={{ margin: 0 }}>{t('geen')}</p>}
      </div>
    </>
  );
}
